import React, { useEffect, useRef, useState } from 'react';
import { Select } from 'equasion-lib';
import { FiEdit3, FiList, FiPlus } from 'react-icons/fi';
import {
  carrierDimensions,
  customDimensionFields,
  formatDimensions,
  packageDimensions,
  savedSizeOptions,
  validateCustomDimensions,
  type CustomDimensionField,
  type PackageSizeMode,
  type ShipPackageDraft
} from './packageSize';

const FIELD_NAMES: Record<CustomDimensionField, string> = {
  length: 'Length',
  width: 'Width',
  height: 'Height'
};

type PackageSizeFieldProps = {
  pkg: ShipPackageDraft;
  /** The saved sizes for this package's type, largest first. */
  savedSizes: number[][];
  onChange: (patch: Partial<ShipPackageDraft>) => void;
  /** Show every field's error, touched or not (the operator pressed Save). */
  showAllErrors: boolean;
  /** Called on Enter in the last custom field — the sheet's Save. */
  onSubmit: () => void;
  /**
   * Packing: a size typed here is ADDED to the vendor's package library when the sheet is saved
   * (the caller does the add), so the option reads "Add new size". Off (the Box Sizes correction
   * page): a one-off "Custom size" for this package only.
   */
  addsToLibrary?: boolean;
  /** Why the last add to the library failed (shown under the size). */
  addError?: string | null;
};

/**
 * The package sheet's size: one of the vendor's saved sizes, or one entered by hand (L × W × H for
 * a box, L × W for a poly bag) for a carton that matches none of them — usually the manufacturer's
 * own box. At packing (`addsToLibrary`) that size joins the saved sizes for next time; on the Box
 * Sizes page it is a one-off. Both choices stay on screen as a segmented control, so the saved list
 * is always one tap away, and switching keeps what was typed.
 */
const PackageSizeField = ({
  pkg,
  savedSizes,
  onChange,
  showAllErrors,
  onSubmit,
  addsToLibrary = false,
  addError = null
}: PackageSizeFieldProps) => {
  const { type, size, sizeMode, custom = {} } = pkg;
  const isCustom = sizeMode === 'custom';
  const hasSaved = savedSizes.length > 0;
  const fields = customDimensionFields(type);
  const { errors, dimensions } = validateCustomDimensions(custom, type);
  const [touched, setTouched] = useState<Partial<Record<CustomDimensionField, boolean>>>(
    {}
  );
  const firstFieldRef = useRef<HTMLInputElement | null>(null);
  const focusOnCustom = useRef(false);

  // Switching to Custom puts the cursor in Length, ready for the tape measure.
  useEffect(() => {
    if (isCustom && focusOnCustom.current) firstFieldRef.current?.focus();
    focusOnCustom.current = false;
  }, [isCustom]);

  const chooseMode = (mode: PackageSizeMode) => {
    if (mode === sizeMode) return;
    if (mode === 'custom') {
      focusOnCustom.current = true;
      onChange({ sizeMode: 'custom' });
    } else {
      // Back to the list: keep the box that was picked, else the largest.
      const keys = savedSizeOptions(savedSizes).map(([key]) => key);
      onChange({ sizeMode: 'saved', size: size && keys.includes(size) ? size : keys[0] });
    }
  };

  const visibleError = (field: CustomDimensionField) =>
    (showAllErrors || touched[field]) && errors[field];
  const fractional = !!dimensions && dimensions.some((n) => !Number.isInteger(n));

  const modeOption = (mode: PackageSizeMode, label: string, Icon: typeof FiList) => {
    const selected = (mode === 'custom') === isCustom;
    return (
      <button
        type="button"
        className={`package-size-mode__option${selected ? ' is-selected' : ''}`}
        aria-pressed={selected}
        onClick={() => chooseMode(mode)}
      >
        <Icon aria-hidden />
        {label}
      </button>
    );
  };

  return (
    <div className="package-size">
      <div className="package-sheet__heading">Size</div>
      {hasSaved ? (
        <div className="package-size-mode" role="group" aria-label="Package size">
          {modeOption('saved', 'Saved sizes', FiList)}
          {addsToLibrary
            ? modeOption('custom', 'Add new size', FiPlus)
            : modeOption('custom', 'Custom size', FiEdit3)}
        </div>
      ) : addsToLibrary ? (
        <p className="package-size__note">
          No saved {type === 'bag' ? 'poly bag' : 'box'} sizes yet — enter this
          one&rsquo;s size and it&rsquo;s saved for next time.
        </p>
      ) : (
        <p className="package-size__note">
          No saved {type === 'bag' ? 'poly bag' : 'box'} sizes — enter this one&rsquo;s
          size. Saved sizes are set up in RxERP Settings → Shipping Methods.
        </p>
      )}

      {!isCustom ? (
        <>
          <Select
            form={false}
            label={
              type === 'bag'
                ? 'Poly bag size (largest first)'
                : 'Box size (largest first)'
            }
            value={size ?? ''}
            defaultValue={size ?? ''}
            onChange={({ value }) => onChange({ size: value })}
            options={savedSizeOptions(savedSizes)}
          />
          {showAllErrors && !packageDimensions(pkg) && (
            <ul className="package-dims__errors" role="alert">
              <li>
                {addsToLibrary
                  ? 'Choose a size, or add a new one.'
                  : 'Choose a size, or enter a custom one.'}
              </li>
            </ul>
          )}
        </>
      ) : (
        <>
          <div className="package-dims" role="group" aria-label="Custom size in inches">
            {fields.map((field, i) => {
              const error = visibleError(field);
              return (
                <React.Fragment key={field}>
                  {i > 0 && (
                    <span className="package-dims__times" aria-hidden="true">
                      ×
                    </span>
                  )}
                  <label className={`package-dims__field${error ? ' is-invalid' : ''}`}>
                    <span className="package-dims__label">{FIELD_NAMES[field]}</span>
                    <input
                      ref={i === 0 ? firstFieldRef : undefined}
                      name={`package-${field}`}
                      // Text with a decimal keypad, not type="number": a number field silently
                      // turns "abc" or a blank into nothing / 0, and this one must say why it is wrong.
                      type="text"
                      inputMode="decimal"
                      autoComplete="off"
                      enterKeyHint={i === fields.length - 1 ? 'done' : 'next'}
                      value={custom[field] ?? ''}
                      aria-invalid={!!error}
                      aria-describedby={error ? `package-${field}-error` : undefined}
                      onChange={(e) =>
                        onChange({ custom: { ...custom, [field]: e.target.value } })
                      }
                      onBlur={() => setTouched((t) => ({ ...t, [field]: true }))}
                      onKeyDown={(e) => {
                        if (e.key !== 'Enter') return;
                        e.preventDefault();
                        const next = (
                          e.currentTarget.closest('.package-dims') as HTMLElement
                        )?.querySelectorAll('input')[i + 1] as
                          | HTMLInputElement
                          | undefined;
                        if (next) next.focus();
                        else onSubmit();
                      }}
                    />
                  </label>
                </React.Fragment>
              );
            })}
            <span className="package-dims__unit">in</span>
          </div>
          {fields.some(visibleError) && (
            <ul className="package-dims__errors" role="alert">
              {fields
                .filter((field) => visibleError(field))
                .map((field) => (
                  <li key={field} id={`package-${field}-error`}>
                    {errors[field]}
                  </li>
                ))}
            </ul>
          )}
          {addError && (
            <ul className="package-dims__errors" role="alert">
              <li>{addError}</li>
            </ul>
          )}
          {addsToLibrary && (
            <p className="package-size__note">
              Saving adds this size to your saved {type === 'bag' ? 'poly bag' : 'box'}{' '}
              sizes, so it&rsquo;s in the list from now on.
            </p>
          )}
          {fractional && dimensions && (
            <p className="package-size__note">
              Carriers measure in whole inches, so this ships as{' '}
              <strong>{formatDimensions(carrierDimensions(dimensions))}</strong>.
            </p>
          )}
        </>
      )}
    </div>
  );
};

export default PackageSizeField;
